import { useState, useRef, useCallback } from 'react'
import { Upload, FileText, X, ChevronDown, ChevronRight } from 'lucide-react'
import { Card, CardContent, Badge } from '@personic/shared'

export interface QAPair {
  question: string
  answer: string
}

interface QuestionnaireUploaderProps {
  onContentParsed: (content: string, pairs: QAPair[]) => void
  initialContent?: string
}

function parseQAPairs(text: string): QAPair[] {
  const pairs: QAPair[] = []
  const lines = text.trim().split('\n')
  let currentQ: string | null = null

  for (const line of lines) {
    const trimmed = line.trim()
    if (!trimmed) continue

    // Q: / A: format
    if (/^q\s*[:\.]\s*/i.test(trimmed)) {
      currentQ = trimmed.replace(/^q\s*[:\.]\s*/i, '').trim()
    } else if (/^a\s*[:\.]\s*/i.test(trimmed) && currentQ) {
      pairs.push({ question: currentQ, answer: trimmed.replace(/^a\s*[:\.]\s*/i, '').trim() })
      currentQ = null
    }
    // Numbered format: "1. Question" / "Answer on next line"
    else if (/^\d+[\.\)]\s+/.test(trimmed) && !currentQ) {
      currentQ = trimmed.replace(/^\d+[\.\)]\s+/, '').trim()
    } else if (currentQ && !trimmed.match(/^\d+[\.\)]\s+/)) {
      pairs.push({ question: currentQ, answer: trimmed })
      currentQ = null
    }
  }

  return pairs
}

export function QuestionnaireUploader({ onContentParsed, initialContent: _initialContent }: QuestionnaireUploaderProps) {
  const [dragActive, setDragActive] = useState(false)
  const [fileName, setFileName] = useState<string | null>(null)
  const [parsedPairs, setParsedPairs] = useState<QAPair[]>([])
  const [showParsed, setShowParsed] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const processText = useCallback((text: string, name?: string) => {
    const pairs = parseQAPairs(text)
    setParsedPairs(pairs)
    if (name) setFileName(name)

    // If we parsed Q&A pairs, format as Q:/A: for the distiller
    if (pairs.length > 0) {
      const formatted = pairs
        .map((p) => `Q: ${p.question}\nA: ${p.answer}`)
        .join('\n\n')
      onContentParsed(formatted, pairs)
    } else {
      // Pass raw text if no Q&A pairs detected
      onContentParsed(text, [])
    }
  }, [onContentParsed])

  const handleFile = useCallback(async (file: File) => {
    if (file.type === 'text/plain' || file.name.endsWith('.txt')) {
      const text = await file.text()
      processText(text, file.name)
    } else {
      // For unsupported file types, just read as text
      const text = await file.text()
      processText(text, file.name)
    }
  }, [processText])

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    if (e.dataTransfer.files?.[0]) {
      handleFile(e.dataTransfer.files[0])
    }
  }, [handleFile])

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      handleFile(e.target.files[0])
    }
  }, [handleFile])

  const handleClear = () => {
    setFileName(null)
    setParsedPairs([])
    setShowParsed(false)
    onContentParsed('', [])
  }

  return (
    <div className="space-y-3">
      {/* Drop zone */}
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
          dragActive
            ? 'border-copper-500 bg-copper-50'
            : 'border-warm-300 hover:border-warm-400 hover:bg-warm-50'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".txt,.csv"
          onChange={handleFileInput}
          className="hidden"
        />
        <Upload className="w-8 h-8 mx-auto mb-2 text-warm-400" />
        <p className="text-sm text-warm-500">
          Drop a file here or <span className="text-copper-600 font-medium">browse</span>
        </p>
        <p className="text-xs text-warm-400 mt-1">Supports .txt files with Q:/A: or numbered format</p>
      </div>

      {/* File info and parsed results */}
      {fileName && (
        <div className="flex items-center justify-between p-3 bg-warm-50 rounded-lg">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-copper-500" />
            <span className="text-sm text-midnight-700 font-medium">{fileName}</span>
            {parsedPairs.length > 0 && (
              <Badge variant="sage" size="sm">
                {parsedPairs.length} Q&A pairs
              </Badge>
            )}
          </div>
          <button onClick={handleClear} className="text-warm-400 hover:text-warm-600">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Parsed Q&A preview */}
      {parsedPairs.length > 0 && (
        <div>
          <button
            onClick={() => setShowParsed(!showParsed)}
            className="flex items-center gap-1 text-sm text-copper-600 hover:text-copper-700"
          >
            {showParsed ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            {showParsed ? 'Hide' : 'Show'} parsed questions
          </button>

          {showParsed && (
            <Card className="mt-2">
              <CardContent className="p-3 space-y-3 max-h-64 overflow-y-auto">
                {parsedPairs.map((pair, i) => (
                  <div key={i} className="space-y-1">
                    <div className="text-xs font-medium text-copper-600">Q{i + 1}: {pair.question}</div>
                    <div className="text-xs text-midnight-700 pl-4">{pair.answer}</div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  )
}
